import {createQueryKeys} from '@lukemorales/query-key-factory'
import {api} from 'api'
import type {BookingFilterShowKind} from 'app/main/Booking/types'
import {beforeAndAfter} from 'data/helpers/beforeAndAfter'
import type {DateString} from 'types/generic'
import {queryKeysFromParams} from './queryKeysFromParams'

type SingleAct = {actId: number}
type MultpleActs = {showKind: BookingFilterShowKind}
type SingleMonth = {
  month: DateString
}
type MultipleMonths = {
  startMonth: DateString
  endMonth: DateString
}

export type SingleMonthInColumnsProps = SingleAct & MultipleMonths
export type SingleMonthProps = SingleAct & SingleMonth
export type SingleListProps = SingleAct & MultipleMonths
export type MultipleActsInColumnsProps = MultpleActs & SingleMonth
export type MultipleMonthsInColumnsProps = MultpleActs & MultipleMonths
export type MultipleMonthProps = MultpleActs & SingleMonth
export type MultipleListProps = MultpleActs & MultipleMonths

export const bookingEvents = createQueryKeys('bookingEvents', {
  // 'single/months-in-columns'
  singleMonthInColumns: (props: SingleMonthInColumnsProps) => {
    const queryProps = {
      ...beforeAndAfter('month', props.startMonth, props.endMonth),
      actIds: [props.actId ?? -1],
    }

    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list(queryProps),
    }
  },
  // 'single/month'
  singleMonth: (props: SingleMonthProps) => {
    const queryProps = {
      ...beforeAndAfter('extendedMonth', props.month),
      actIds: [props.actId],
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list(queryProps),
    }
  },
  // 'single/list'
  singleList: (props: SingleListProps) => {
    const queryProps = {
      ...beforeAndAfter('month', props.startMonth, props.endMonth),
      actIds: [props.actId ?? -1],
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list({...queryProps, eventTypes: 'shows'}),
    }
  },
  // 'multiple/acts-in-columns'
  multipleActsInColumns: (props: MultipleActsInColumnsProps) => {
    const queryProps = {
      ...beforeAndAfter('month', props.month),
      actIds:
        props.showKind === 'represented_by_me'
          ? ('represented_by_me' as const)
          : undefined,
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list(queryProps),
    }
  },
  // 'multiple/months-in-columns'
  multipleMonthsInColumns: (props: MultipleMonthsInColumnsProps) => {
    const queryProps = {
      ...beforeAndAfter('month', props.startMonth, props.endMonth),
      actIds:
        props.showKind === 'represented_by_me'
          ? ('represented_by_me' as const)
          : undefined,
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list(queryProps),
    }
  },
  // 'multiple/month'
  multipleMonth: (props: MultipleMonthProps) => {
    const queryProps = {
      ...beforeAndAfter('extendedMonth', props.month),
      actIds:
        props.showKind === 'represented_by_me'
          ? ('represented_by_me' as const)
          : undefined,
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list(queryProps),
    }
  },
  // 'multiple/list'
  multipleList: (props: MultipleListProps) => {
    const queryProps = {
      ...beforeAndAfter('month', props.startMonth, props.endMonth),
      actIds:
        props.showKind === 'represented_by_me'
          ? ('represented_by_me' as const)
          : undefined,
    }
    return {
      queryKey: queryKeysFromParams(props),
      queryFn: () => api.planner.list({...queryProps, eventTypes: 'shows'}),
    }
  },
})
